// Drawer — reusable low-level off-canvas panel primitive
// (RFC-0014 Phase 1 Task 1.1 — reusable Drawer primitive (reused in Phase 3)).
//
// View-agnostic: it knows NOTHING about nav content. It only renders `children`
// inside an accessible off-canvas panel with a scrim. Phase 1 (mobile nav) and
// Phase 3 (doc-nav drawer) both consume this UNCHANGED via the public API below.
//
// Accessibility (the crux):
//   - Scrim (`role="presentation"`) closes on click; the panel stops propagation.
//   - Panel is a `role="dialog"` + `aria-modal="true"` + `aria-label`.
//   - Esc closes; Tab is trapped within the panel (first/last wrap) — same
//     FOCUSABLE_SELECTOR + logic as command-palette.tsx.
//   - Focus moves into the panel on open and is restored to the previously
//     focused element on close/unmount; body scroll is locked while mounted.
//   - Transitions are suppressed under `prefers-reduced-motion: reduce`.
//
// Mount gate: like the command palette, the component renders `null` when closed,
// so focus capture/restore and scroll-lock run on mount/unmount for every close
// path (scrim click, Esc, parent route-change, unmount).

import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

/** Focusable elements for the focus trap (mirrors command-palette.tsx). */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** True when the user has NOT requested reduced motion (guarded for jsdom). */
function motionAllowed(): boolean {
  try {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // jsdom (and other environments without matchMedia) → treat as motion allowed.
    return true;
  }
}

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Accessible label for the dialog (aria-label on the panel). */
  label: string;
  /** Which edge the panel slides from. Default 'left'. */
  side?: 'left' | 'right';
  children: ReactNode;
}

/**
 * Mount gate: the panel (with its effects) only exists while open, so focus
 * capture/restore and scroll-lock happen on mount/unmount — exactly like the
 * command-palette overlay's mount-gate.
 */
export function Drawer(props: DrawerProps): React.ReactElement | null {
  if (!props.isOpen) return null;
  return <DrawerPanel {...props} />;
}

function DrawerPanel({ onClose, label, side = 'left', children }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // Capture the invoking element on open; move focus into the panel; restore on close.
  useEffect(() => {
    restoreRef.current = document.activeElement as HTMLElement | null;
    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable && focusable.length > 0) {
      focusable[0].focus();
    } else {
      panelRef.current?.focus();
    }
    return () => {
      restoreRef.current?.focus?.();
    };
  }, []);

  // Body scroll-lock while mounted; restore the PREVIOUS value on every close path
  // (unmount cleanup covers scrim click, Esc, parent route-change, and unmount).
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  function onDialogKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }

    // Focus trap: keep Tab within the panel.
    if (event.key === 'Tab') {
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) {
        // Nothing focusable inside — keep focus on the panel itself.
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeEl = document.activeElement;
      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const sidePosition = side === 'right' ? 'right-0 top-0 h-full border-l' : 'left-0 top-0 h-full border-r';
  const transition = motionAllowed() ? 'transition-transform duration-200 ease-out' : '';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={`fixed ${sidePosition} w-[85vw] max-w-xs overflow-y-auto border-border bg-surface shadow-[var(--c-shadow-md)] outline-none ${transition}`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onDialogKeyDown}
      >
        {children}
      </div>
    </div>
  );
}
